import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Landmark, Plug, Plus, Search, Star, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';

interface BrasilApiResult {
  cnpj: string;
  razaoSocial: string;
  nomeFantasia?: string;
}

import { useAuth } from '@/contexts/AuthContext';
import { ApiError, apiFetch } from '@/lib/api';

type Environment = 'SANDBOX' | 'PRODUCTION';

interface ConnectionTestResult {
  ok: boolean;
  detail: string;
  steps?: Array<{ nome: string; ok: boolean; detalhe?: string }>;
}

export interface BankAccountPublic {
  id: string;
  providerName: string;
  name: string;
  bankCode: string | null;
  bankName: string | null;
  agencia: string | null;
  conta: string | null;
  contaDigito: string | null;
  beneficiarioNome: string;
  beneficiarioCpfCnpj: string;
  carteira: string | null;
  certificateId: string | null;
  environment: Environment;
  isActive: boolean;
  isDefault: boolean;
}

export function BankAccountsPage() {
  const { user } = useAuth();
  const canManage = user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN';
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['bank-accounts'],
    queryFn: () => apiFetch<BankAccountPublic[]>('/bank-accounts'),
  });

  const setDefault = useMutation({
    mutationFn: (id: string) =>
      apiFetch<BankAccountPublic>(`/bank-accounts/${id}/default`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bank-accounts'] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/bank-accounts/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bank-accounts'] }),
  });

  // Resultado do ultimo teste, por conta — fica visivel no card ate sair da tela.
  const [testes, setTestes] = useState<Record<string, ConnectionTestResult>>({});
  const [testando, setTestando] = useState<string | null>(null);

  const testar = useMutation({
    mutationFn: (id: string) =>
      apiFetch<ConnectionTestResult>(`/billing/accounts/${id}/test-connection`, {
        method: 'POST',
      }),
    onMutate: (id: string) => setTestando(id),
    onSettled: () => setTestando(null),
    onSuccess: (res, id) => setTestes((s) => ({ ...s, [id]: res })),
    onError: (err, id) =>
      setTestes((s) => ({
        ...s,
        [id]: { ok: false, detail: err instanceof Error ? err.message : 'Falha no teste' },
      })),
  });

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Contas bancárias / Cobrança</h1>
          <p className="text-sm text-muted-foreground">
            Configure providers de cobrança (boleto + PIX). Hoje suportamos <strong>Asaas</strong>.
            API keys são cifradas com AES-256-GCM.
          </p>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" /> Nova conta
          </button>
        )}
      </header>

      {isLoading && <p className="text-sm text-muted-foreground">Carregando…</p>}
      {isError && <p className="text-sm text-destructive">Erro ao carregar.</p>}

      {data && data.length === 0 && (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-border bg-card p-12 text-center">
          <Landmark className="h-10 w-10 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">Nenhuma conta cadastrada.</p>
        </div>
      )}

      {data && data.length > 0 && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {data.map((a) => (
            <article key={a.id} className="rounded-lg border border-border bg-card p-4 space-y-3">
              <header className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-medium truncate">{a.name}</h3>
                  <p className="text-xs text-muted-foreground">
                    {a.providerName} •{' '}
                    <span
                      className={
                        a.environment === 'PRODUCTION'
                          ? 'text-emerald-600 font-medium'
                          : 'text-amber-600'
                      }
                    >
                      {a.environment === 'PRODUCTION' ? 'Produção' : 'Sandbox'}
                    </span>
                  </p>
                </div>
                {a.isDefault && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                    <Star className="h-3 w-3" /> Padrão
                  </span>
                )}
              </header>

              <dl className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Beneficiário</dt>
                  <dd>{a.beneficiarioNome}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">CPF/CNPJ</dt>
                  <dd className="font-mono">{a.beneficiarioCpfCnpj}</dd>
                </div>
                {a.bankName && (
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Banco</dt>
                    <dd>{a.bankName}</dd>
                  </div>
                )}
              </dl>

              <TestResultBox result={testes[a.id]} />

              <div className="flex items-center gap-2">
                {canManage && (
                  <button
                    type="button"
                    data-dica="Autentica e faz uma consulta de leitura — não emite nada"
                    disabled={testando === a.id}
                    onClick={() => testar.mutate(a.id)}
                    className="flex items-center gap-1 rounded-md border border-input px-2 py-1 text-xs hover:bg-accent disabled:opacity-50"
                  >
                    <Plug className="h-3 w-3" />
                    {testando === a.id ? 'Testando…' : 'Testar conexão'}
                  </button>
                )}
                {canManage && !a.isDefault && (
                  <button
                    type="button"
                    onClick={() => setDefault.mutate(a.id)}
                    className="flex items-center gap-1 rounded-md border border-input px-2 py-1 text-xs hover:bg-accent"
                  >
                    <Star className="h-3 w-3" /> Tornar padrão
                  </button>
                )}
                {canManage && !a.isDefault && (
                  <button
                    type="button"
                    data-dica="Excluir"
                    onClick={() => {
                      if (confirm(`Excluir conta "${a.name}"?`)) remove.mutate(a.id);
                    }}
                    className="ml-auto rounded-md p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateBankAccountDialog
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            setShowCreate(false);
            qc.invalidateQueries({ queryKey: ['bank-accounts'] });
          }}
        />
      )}
    </div>
  );
}

function CreateBankAccountDialog({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: 'Asaas (Sandbox)',
    providerName: 'ASAAS',
    bankCode: '',
    bankName: '',
    agencia: '',
    conta: '',
    contaDigito: '',
    beneficiarioNome: '',
    beneficiarioCpfCnpj: '',
    carteira: '109',
    apiKey: '',
    webhookSecret: '',
    // Campos especificos pra Itau/Sicoob:
    certificateId: '',
    sandboxToken: '',
    pfxBase64: '',
    pfxPassword: '',
    convenio: '',
    beneficiaryCode: '',
    clientSecret: '',
    oauthScopes: '',
    environment: 'SANDBOX' as Environment,
    isDefault: true,
  });
  const [error, setError] = useState<string | null>(null);
  const [lookupErr, setLookupErr] = useState<string | null>(null);

  const lookup = useMutation({
    mutationFn: (cnpj: string) =>
      apiFetch<BrasilApiResult>(`/clients/lookup-cnpj/${cnpj.replace(/\D/g, '')}`),
    onSuccess: (r) => {
      setForm((s) => ({
        ...s,
        beneficiarioNome: r.nomeFantasia || r.razaoSocial || s.beneficiarioNome,
      }));
      setLookupErr(null);
    },
    onError: () => setLookupErr('CNPJ não encontrado na BrasilAPI.'),
  });
  const isBankDirect = form.providerName === 'ITAU' || form.providerName === 'SICOOB';

  // Certificados ja cadastrados — o mesmo A1 da NFSe serve pro mTLS bancario.
  const certificates = useQuery({
    queryKey: ['certificates', 'select-bank'],
    queryFn: () =>
      apiFetch<
        Array<{
          id: string;
          name: string;
          subjectCn: string | null;
          expiresAt: string;
          isDefault: boolean;
        }>
      >('/certificates'),
    enabled: isBankDirect,
  });

  const save = useMutation({
    mutationFn: () =>
      apiFetch<BankAccountPublic>('/bank-accounts', {
        method: 'POST',
        body: JSON.stringify({
          ...form,
          beneficiarioCpfCnpj: form.beneficiarioCpfCnpj.replace(/\D/g, ''),
          webhookSecret: form.webhookSecret || undefined,
          bankCode: form.bankCode || undefined,
          bankName: form.bankName || undefined,
          agencia: form.agencia || undefined,
          conta: form.conta || undefined,
          contaDigito: form.contaDigito || undefined,
          carteira: form.carteira || undefined,
          certificateId: form.certificateId || undefined,
          sandboxToken: form.sandboxToken || undefined,
          // Certificado cadastrado substitui o PFX avulso — nao envia os dois.
          pfxBase64: form.certificateId ? undefined : form.pfxBase64 || undefined,
          pfxPassword: form.certificateId ? undefined : form.pfxPassword || undefined,
          convenio: form.convenio || undefined,
          beneficiaryCode: form.beneficiaryCode || undefined,
          clientSecret: form.clientSecret || undefined,
          oauthScopes: form.oauthScopes.trim() || undefined,
        }),
      }),
    onSuccess: onSaved,
    onError: (err) =>
      setError(err instanceof ApiError ? err.body : err instanceof Error ? err.message : 'erro'),
  });

  async function handlePfxUpload(file: File) {
    const ab = await file.arrayBuffer();
    // toBase64
    const bytes = new Uint8Array(ab);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
    const b64 = btoa(binary);
    setForm((s) => ({ ...s, pfxBase64: b64 }));
  }

  function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    save.mutate();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg border border-border bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-medium">Nova conta de cobrança</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field label="Nome amigável *">
              <input
                required
                className="input"
                value={form.name}
                onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
              />
            </Field>
            <Field label="Provider *">
              <select
                required
                className="input"
                value={form.providerName}
                onChange={(e) => setForm((s) => ({ ...s, providerName: e.target.value }))}
              >
                <option value="ASAAS">Asaas (gateway, simples)</option>
                <option value="ITAU">Itaú (API Cobrança PJ)</option>
                <option value="SICOOB">Sicoob (Cobrança Bancária v3)</option>
              </select>
            </Field>
            <Field label="Ambiente *">
              <select
                className="input"
                value={form.environment}
                onChange={(e) =>
                  setForm((s) => ({ ...s, environment: e.target.value as Environment }))
                }
              >
                <option value="SANDBOX">Sandbox</option>
                <option value="PRODUCTION">Produção</option>
              </select>
            </Field>
            <Field label="Beneficiário (nome) *">
              <input
                required
                className="input"
                value={form.beneficiarioNome}
                onChange={(e) => setForm((s) => ({ ...s, beneficiarioNome: e.target.value }))}
              />
            </Field>
            <Field label="CPF/CNPJ do beneficiário *">
              <div className="flex gap-2">
                <input
                  required
                  className="input font-mono"
                  value={form.beneficiarioCpfCnpj}
                  onChange={(e) => setForm((s) => ({ ...s, beneficiarioCpfCnpj: e.target.value }))}
                />
                <button
                  type="button"
                  disabled={lookup.isPending || form.beneficiarioCpfCnpj.replace(/\D/g, '').length !== 14}
                  onClick={() => lookup.mutate(form.beneficiarioCpfCnpj)}
                  title="Buscar dados do CNPJ na BrasilAPI"
                  className="flex items-center gap-1 rounded-md border border-input bg-background px-3 text-xs whitespace-nowrap hover:bg-accent disabled:opacity-50"
                >
                  <Search className="h-3 w-3" /> {lookup.isPending ? 'Buscando…' : 'Buscar'}
                </button>
              </div>
              {lookupErr && <span className="text-xs text-destructive">{lookupErr}</span>}
            </Field>
          </div>

          <Field
            label={isBankDirect ? 'Client ID do aplicativo *' : 'API key do provider *'}
          >
            <input
              required
              type="password"
              className="input font-mono"
              value={form.apiKey}
              onChange={(e) => setForm((s) => ({ ...s, apiKey: e.target.value }))}
              placeholder={isBankDirect ? '00000000-0000-0000-0000-000000000000' : '$aact_...'}
            />
            <span className="text-[10px] text-muted-foreground">
              {form.providerName === 'SICOOB'
                ? 'O Client ID do aplicativo em developers.sicoob.com.br. Cifrado com AES-256-GCM antes de persistir.'
                : 'Cifrada com AES-256-GCM antes de persistir.'}
            </span>
          </Field>

          <Field label="Webhook secret (opcional)">
            <input
              type="password"
              className="input font-mono"
              value={form.webhookSecret}
              onChange={(e) => setForm((s) => ({ ...s, webhookSecret: e.target.value }))}
              placeholder={
                isBankDirect
                  ? 'Segredo do webhook cadastrado no portal do banco'
                  : 'Token do webhook configurado no Asaas'
              }
            />
            <span className="text-[10px] text-muted-foreground">
              Usado só para conferir a assinatura das notificações de pagamento.
              Não é o segredo de login na API.
            </span>
          </Field>

          {/* Bloco extra: aparece SO para Itau/Sicoob */}
          {isBankDirect && (
            <fieldset className="space-y-3 rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
              <legend className="text-xs font-semibold uppercase tracking-wider text-amber-700">
                Credenciais bancárias mTLS — obrigatório para {form.providerName}
              </legend>

              {/* Sandbox do Sicoob e mock: token fixo do portal, sem OAuth2/mTLS */}
              {form.environment === 'SANDBOX' && form.providerName === 'SICOOB' && (
                <Field label="Access token do sandbox (Bearer)">
                  <input
                    className="input font-mono text-xs"
                    placeholder="1301865f-c6bc-38f3-9f49-666dbcfc59c3"
                    value={form.sandboxToken}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, sandboxToken: e.target.value }))
                    }
                  />
                  <span className="text-[10px] text-muted-foreground">
                    Copie da página <b>Sandbox</b> do portal Sicoob. Com ele
                    preenchido, o sandbox dispensa certificado — em produção o
                    certificado volta a ser obrigatório.
                  </span>
                </Field>
              )}

              <Field
                label={
                  form.sandboxToken
                    ? 'Certificado digital (opcional no sandbox)'
                    : 'Certificado digital *'
                }
              >
                <select
                  className="input"
                  value={form.certificateId}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, certificateId: e.target.value }))
                  }
                >
                  <option value="">— Enviar um PFX avulso —</option>
                  {(certificates.data ?? []).map((c) => {
                    const venc = new Date(c.expiresAt);
                    const vencido = venc.getTime() < Date.now();
                    return (
                      <option key={c.id} value={c.id}>
                        {c.name}
                        {c.subjectCn ? ` — ${c.subjectCn}` : ''}
                        {` (vence ${venc.toLocaleDateString('pt-BR')})`}
                        {vencido ? ' ⚠ VENCIDO' : ''}
                      </option>
                    );
                  })}
                </select>
                <span className="text-[10px] text-muted-foreground">
                  Reaproveita o mesmo A1 usado na NFSe — senha e renovação ficam
                  num lugar só.
                </span>
              </Field>

              {/* PFX avulso: so quando nenhum certificado cadastrado foi escolhido */}
              {!form.certificateId && !form.sandboxToken && (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <Field label="Arquivo PFX/P12 *">
                    <input
                      type="file"
                      accept=".pfx,.p12,application/x-pkcs12"
                      required={isBankDirect && !form.pfxBase64}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) void handlePfxUpload(f);
                      }}
                      className="text-xs"
                    />
                    {form.pfxBase64 && (
                      <span className="text-[10px] text-emerald-700">
                        ✓ PFX carregado ({Math.round(form.pfxBase64.length * 0.75)} bytes)
                      </span>
                    )}
                  </Field>

                  <Field label="Senha do PFX *">
                    <input
                      type="password"
                      required={isBankDirect && !form.certificateId}
                      className="input font-mono"
                      value={form.pfxPassword}
                      onChange={(e) => setForm((s) => ({ ...s, pfxPassword: e.target.value }))}
                    />
                  </Field>
                </div>
              )}

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">

                <Field
                  label={
                    form.providerName === 'SICOOB'
                      ? 'Número do cliente Sicoob *'
                      : 'ID do beneficiário Itaú *'
                  }
                >
                  <input
                    required={isBankDirect}
                    className="input font-mono"
                    placeholder="ex.: 25546454"
                    value={form.beneficiaryCode}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, beneficiaryCode: e.target.value }))
                    }
                  />
                  <span className="text-[10px] text-muted-foreground">
                    {form.providerName === 'SICOOB'
                      ? 'É o número que identifica seu convênio de cobrança — o `numeroCliente` da API. Peça ao gerente da cooperativa: "qual é o número do cliente do meu convênio de cobrança para a API v3?".'
                      : 'Identificador do beneficiário no banco.'}
                  </span>
                </Field>

                <Field
                  label={
                    form.providerName === 'SICOOB'
                      ? 'Número do contrato (opcional)'
                      : 'Número do convênio *'
                  }
                >
                  <input
                    // Opcional no Sicoob: a API v3 identifica tudo pelo
                    // numeroCliente. Guardamos só como referência do contrato.
                    required={isBankDirect && form.providerName !== 'SICOOB'}
                    className="input font-mono"
                    value={form.convenio}
                    onChange={(e) => setForm((s) => ({ ...s, convenio: e.target.value }))}
                  />
                  {form.providerName === 'SICOOB' && (
                    <span className="text-[10px] text-muted-foreground">
                      Não é usado nas chamadas à API — fica só como anotação do contrato. Se não
                      tiver em mãos, deixe vazio.
                    </span>
                  )}
                </Field>
              </div>

              <Field label="Escopos OAuth (opcional)">
                <input
                  className="input font-mono text-xs"
                  value={form.oauthScopes}
                  onChange={(e) => setForm((s) => ({ ...s, oauthScopes: e.target.value }))}
                  placeholder="boletos_inclusao boletos_consulta boletos_alteracao …"
                />
                <span className="text-[10px] text-muted-foreground">
                  Deixe vazio para usar os seis padrões (inclusão, consulta e alteração
                  de boletos e de webhooks). Preencha só se o aplicativo no portal tiver
                  uma lista diferente — pedir um escopo que o app não tem faz o banco
                  recusar o login inteiro.
                </span>
              </Field>

              <Field label="Client secret OAuth (só se o banco exigir)">
                <input
                  type="password"
                  className="input font-mono"
                  value={form.clientSecret}
                  onChange={(e) => setForm((s) => ({ ...s, clientSecret: e.target.value }))}
                  placeholder="deixe vazio no Sicoob"
                />
                <span className="text-[10px] text-muted-foreground">
                  No Sicoob não existe: quem prova a identidade é o próprio
                  certificado. Preencher aqui por engano faz o login falhar.
                </span>
              </Field>

              <p className="text-[11px] text-amber-800">
                Tudo cifrado com AES-256-GCM no banco. O PFX é o mesmo certificado A1
                emitido pela ICP-Brasil que você usa na NFSe (ou um separado, se a
                empresa preferir isolar).
              </p>
            </fieldset>
          )}

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isDefault}
              onChange={(e) => setForm((s) => ({ ...s, isDefault: e.target.checked }))}
            />
            Definir como padrão
          </label>

          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={save.isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {save.isPending ? 'Salvando…' : 'Criar'}
            </button>
          </div>
        </form>

        <style>{`
          .input {
            width: 100%;
            border: 1px solid hsl(var(--input));
            background: hsl(var(--background));
            padding: 0.5rem 0.75rem;
            border-radius: 0.375rem;
            font-size: 0.875rem;
          }
          .input:focus { outline: none; box-shadow: 0 0 0 2px hsl(var(--ring) / 0.4); }
        `}</style>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="space-y-1 block">
      <span className="text-xs font-medium">{label}</span>
      {children}
    </label>
  );
}

/** Caixa com o resultado do ultimo teste de conexao da conta. */
function TestResultBox({ result }: { result?: ConnectionTestResult }) {
  if (!result) return null;
  return (
    <div
      className={`rounded-md border p-2 text-[11px] ${
        result.ok
          ? 'border-emerald-500/40 bg-emerald-500/5 text-emerald-800'
          : 'border-destructive/40 bg-destructive/5 text-destructive'
      }`}
    >
      <p className="font-medium">{result.detail}</p>
      {result.steps && result.steps.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {result.steps.map((st) => (
            <li key={st.nome}>
              {st.ok ? '✓' : '✗'} {st.nome}
              {st.detalhe ? ` — ${st.detalhe}` : ''}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
